/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { ColumnDef } from '@tanstack/react-table'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { LocationJson } from 'app/types/entities'
import { logError } from 'lib/logging'
import { defaultColumns } from './AiAgentIndexComponent'
import { useLocationStore } from 'app/stores/StoreContext'

export type UseAiAgentIndexReturn = {
  data: {
    locations: { hits: LocationJson[]; total: number } | undefined
    aiCalls: Record<string, { count: number; last?: string }>
    aiScheduledAppointments: Record<string, { count: number; last?: string }>
  }
  columns: ColumnDef<LocationJson, unknown>[]
}

export type UseAiAgentIndexOptions = {
  getLocationHref: (locationId: string) => string
  singleLocationRedirect?: (locationId: string) => string
}

export function useAiAgentIndex(options: UseAiAgentIndexOptions): UseAiAgentIndexReturn {
  const { getLocationHref, singleLocationRedirect } = options
  const navigate = useNavigate()
  const store = useLocationStore()
  // `singleLocationRedirect` is a fresh closure each render; the redirect effect
  // below depends on the filtered location list, not on this callback identity,
  // so it reads the latest value via a ref rather than re-running on every render.
  const singleLocationRedirectRef = useRef(singleLocationRedirect)
  singleLocationRedirectRef.current = singleLocationRedirect
  // Guards against the redirect effect firing more than once for the lifetime
  // of this hook instance: the effect re-runs whenever `aiLocations` is
  // replaced (e.g. store refetch) and React StrictMode deliberately replays
  // effect setup, so without this the redirect could navigate repeatedly.
  const hasRedirectedRef = useRef(false)
  const [extraData, setExtraData] = useState<{
    aiCalls: Record<string, { count: number; last?: string }>
    aiScheduledAppointments: Record<string, { count: number; last?: string }>
  }>({ aiCalls: {}, aiScheduledAppointments: {} })

  useEffect(() => {
    store
      .fetchList({ sort: { field: `name.keyword`, direction: `ASC` } })
      .then((result) => {
        const d = result.data as any
        if (d?.aiCalls || d?.aiScheduledAppointments) {
          setExtraData({ aiCalls: d.aiCalls ?? {}, aiScheduledAppointments: d.aiScheduledAppointments ?? {} })
        }
      })
      .catch((err) => logError(`Error fetching AI agent locations`, err))
  }, [])

  // Derive the AI-Agent-filtered list from the observable `store.hits` so the
  // rendered table and the single-location redirect always agree — both read
  // from this same filtered list. Reading `store.hits` directly (not caching in
  // useState) keeps this reactive inside the observer()-wrapped component.
  const aiLocations = useMemo(
    () => (store.hits as unknown as LocationJson[]).filter((loc) => loc.products?.includes(`AI Agent`)),
    [store.hits],
  )

  useEffect(() => {
    if (store.listLoading || !store.list || hasRedirectedRef.current) return
    const redirect = singleLocationRedirectRef.current
    if (redirect && aiLocations.length === 1) {
      hasRedirectedRef.current = true
      navigate(redirect(aiLocations[0].id!), { replace: true })
    }
  }, [store.listLoading, store.list, aiLocations, navigate])

  const columns = useMemo(
    () => defaultColumns(extraData.aiCalls, extraData.aiScheduledAppointments, getLocationHref),
    [extraData.aiCalls, extraData.aiScheduledAppointments, getLocationHref],
  )

  const locations = store.list === undefined ? undefined : { hits: aiLocations, total: aiLocations.length }

  return { data: { locations, ...extraData }, columns }
}
